import { useEffect, useMemo, useState, Fragment } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  UserPlus, Users, Search, Power, KeyRound, Shield, X, Check, Pencil, Copy, Eye, EyeOff,
  RefreshCw, AlertTriangle, Download, FileText, FileSpreadsheet, FileJson,
} from "lucide-react";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRESET_LABELS = {
  director: "Director de Hacienda",
  coord_catastro: "Coordinador de Catastro",
  coord_tributos: "Coordinador de Tributos",
  cajero: "Cajero / Recaudador",
  inspector: "Inspector de Campo",
  aprobador: "Aprobador (4-eyes)",
  soporte: "Help Desk / Soporte",
  auditor: "Auditor (solo lectura)",
  funcionario_basico: "Funcionario Básico",
};

const ROLE_LABELS = {
  admin: "Administrador",
  funcionario: "Funcionario",
  auditor: "Auditor",
};

const ROLE_COLORS = {
  admin: "bg-red-100 text-red-700 border-red-200",
  funcionario: "bg-blue-100 text-blue-700 border-blue-200",
  auditor: "bg-amber-100 text-amber-700 border-amber-200",
};

const AdminUsuariosPage = () => {
  const { user: currentUser, token } = useAuth();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: "", role: "all", is_active: "all" });
  const [catalog, setCatalog] = useState({ modules: [], presets: {}, roles: [] });
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [tempPasswordModal, setTempPasswordModal] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  // Solo admin (verificado dentro del JSX para que no afecte al orden de hooks)
  const isAdmin = currentUser?.role === "admin";
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.role !== "all") params.role = filters.role;
      if (filters.is_active !== "all") params.is_active = filters.is_active === "true";
      const r = await axios.get(`${API}/admin/users`, { params, headers: authHeaders });
      setUsers(r.data.items);
      setTotal(r.data.total);
    } catch (err) {
      toast.error("No se pudieron cargar los funcionarios");
    } finally {
      setLoading(false);
    }
  };

  const fetchCatalog = async () => {
    try {
      const r = await axios.get(`${API}/admin/users/permissions/catalog`, { headers: authHeaders });
      setCatalog(r.data);
    } catch (err) {
      toast.error("No se pudo cargar el catálogo de permisos");
    }
  };

  useEffect(() => {
    if (!isAdmin || !token) return;
    fetchCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, token]);

  useEffect(() => {
    if (!isAdmin || !token) return;
    const t = setTimeout(fetchUsers, 250); // debounce
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.role, filters.is_active, isAdmin, token]);

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-96 text-center">
        <div>
          <Shield className="w-12 h-12 mx-auto text-slate-400 mb-2" />
          <h2 className="text-xl font-semibold text-slate-700">Acceso restringido</h2>
          <p className="text-sm text-slate-500">Esta sección requiere permisos de administrador.</p>
        </div>
      </div>
    );
  }

  const handleCreated = (newUser) => {
    setCreating(false);
    if (newUser.temp_password) {
      setTempPasswordModal({
        email: newUser.email,
        temp_password: newUser.temp_password,
        title: "Funcionario creado correctamente",
      });
    }
    fetchUsers();
  };

  const handleResetPassword = async (user) => {
    try {
      const r = await axios.post(`${API}/admin/users/${user.id}/reset-password`, {}, { headers: authHeaders });
      setTempPasswordModal({
        email: user.email,
        temp_password: r.data.temp_password,
        title: "Clave temporal generada",
        sessions_revoked: r.data.sessions_revoked,
      });
      fetchUsers();
    } catch (err) {
      toast.error("No se pudo resetear la clave");
    }
  };

  const handleDeactivate = async (user) => {
    try {
      const r = await axios.delete(`${API}/admin/users/${user.id}`, { headers: authHeaders });
      toast.success(r.data.message);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo desactivar");
    } finally {
      setConfirmAction(null);
    }
  };

  const handleActivate = async (user) => {
    try {
      await axios.post(`${API}/admin/users/${user.id}/activate`, {}, { headers: authHeaders });
      toast.success("Usuario reactivado");
      fetchUsers();
    } catch (err) {
      toast.error("No se pudo reactivar");
    }
  };

  const handleRevokeSessions = async (user) => {
    try {
      const r = await axios.post(`${API}/admin/users/${user.id}/revoke-sessions`, {}, { headers: authHeaders });
      toast.success(r.data.message);
    } catch (err) {
      toast.error("No se pudieron cerrar sesiones");
    }
  };

  const handleExport = async (format) => {
    try {
      const r = await axios.get(`${API}/admin/users/export`, {
        params: { format },
        headers: authHeaders,
        responseType: "blob",
      });
      // Extraer filename del header content-disposition
      const cd = r.headers["content-disposition"] || "";
      const match = cd.match(/filename="([^"]+)"/);
      const ext = format === "excel" ? "xlsx" : format;
      const filename = match ? match[1] : `funcionarios_${Date.now()}.${ext}`;
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exportado como ${format.toUpperCase()}`);
    } catch (err) {
      toast.error("No se pudo exportar");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-usuarios-page">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-6 h-6 text-blue-700" /> Gestión de Funcionarios
          </h1>
          <p className="text-sm text-slate-500">
            {total} funcionario{total !== 1 ? "s" : ""} registrado{total !== 1 ? "s" : ""} ·
            Permisos modulares editables · Reseteo de claves auditado
          </p>
        </div>
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="btn-export-menu" className="border-slate-300">
                <Download className="w-4 h-4 mr-2" /> Exportar
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel className="text-xs text-slate-500">Formato de exportación</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleExport("pdf")} data-testid="btn-export-pdf" className="cursor-pointer">
                <FileText className="w-4 h-4 mr-2 text-red-600" /> PDF (.pdf)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("excel")} data-testid="btn-export-excel" className="cursor-pointer">
                <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" /> Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("json")} data-testid="btn-export-json" className="cursor-pointer">
                <FileJson className="w-4 h-4 mr-2 text-blue-600" /> JSON (.json)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            onClick={() => setCreating(true)}
            className="bg-blue-700 hover:bg-blue-800 text-white"
            data-testid="btn-nuevo-funcionario"
          >
            <UserPlus className="w-4 h-4 mr-2" /> Nuevo funcionario
          </Button>
        </div>
      </header>

      {/* Filtros */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[260px]">
          <Label className="text-xs text-slate-500 mb-1 block">Buscar</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Nombre, apellido, email o cédula..."
              className="pl-10"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              data-testid="search-input"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">Rol</Label>
          <Select value={filters.role} onValueChange={(v) => setFilters({ ...filters, role: v })}>
            <SelectTrigger className="w-[160px]" data-testid="filter-role">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="admin">Administrador</SelectItem>
              <SelectItem value="funcionario">Funcionario</SelectItem>
              <SelectItem value="auditor">Auditor</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">Estado</Label>
          <Select value={filters.is_active} onValueChange={(v) => setFilters({ ...filters, is_active: v })}>
            <SelectTrigger className="w-[140px]" data-testid="filter-active">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="true">Activos</SelectItem>
              <SelectItem value="false">Desactivados</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="icon" onClick={fetchUsers} title="Refrescar" data-testid="btn-refresh">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Tabla */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
            <tr>
              <th className="px-4 py-3 text-left">Funcionario</th>
              <th className="px-4 py-3 text-left">Rol / Tipo</th>
              <th className="px-4 py-3 text-left">Permisos por módulo</th>
              <th className="px-4 py-3 text-left">Estado</th>
              <th className="px-4 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="text-center py-12 text-slate-400">Cargando...</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-12 text-slate-400">Sin resultados</td></tr>
            ) : users.map((u) => (
              <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`row-${u.id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-slate-900">{u.nombre} {u.apellido}</div>
                  <div className="text-xs text-slate-500">{u.email}</div>
                  {u.cedula && <div className="text-xs text-slate-400">CI: {u.cedula}</div>}
                </td>
                <td className="px-4 py-3">
                  <Badge className={`${ROLE_COLORS[u.role] || "bg-slate-100"} border font-semibold`}>
                    {ROLE_LABELS[u.role] || u.role}
                  </Badge>
                  {u.preset_label && (
                    <div className="text-xs text-slate-500 mt-1">{u.preset_label}</div>
                  )}
                  {u.mfa_enabled && (
                    <div className="text-xs text-emerald-600 mt-1">MFA activo</div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <PermisosBadges permisos={u.permisos} permisosSubmodulos={u.permisos_submodulos} role={u.role} />
                </td>
                <td className="px-4 py-3">
                  {u.is_active ? (
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 border">Activo</Badge>
                  ) : (
                    <Badge className="bg-red-100 text-red-700 border-red-200 border">Desactivado</Badge>
                  )}
                  {u.must_change_password && (
                    <div className="text-xs text-amber-600 mt-1">Pendiente cambiar clave</div>
                  )}
                  {u.last_login && (
                    <div className="text-xs text-slate-400 mt-1">
                      Último login: {new Date(u.last_login).toLocaleDateString("es-VE")}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(u)}
                            title="Editar permisos" data-testid={`btn-edit-${u.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleResetPassword(u)}
                            title="Resetear clave" data-testid={`btn-reset-${u.id}`}>
                      <KeyRound className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleRevokeSessions(u)}
                            title="Cerrar sesiones" data-testid={`btn-revoke-${u.id}`}>
                      <Shield className="w-4 h-4" />
                    </Button>
                    {u.is_active ? (
                      <Button size="sm" variant="ghost"
                              onClick={() => setConfirmAction({ kind: "deactivate", user: u })}
                              title="Desactivar" data-testid={`btn-deact-${u.id}`}
                              disabled={u.id === currentUser?.id}>
                        <Power className="w-4 h-4 text-red-600" />
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => handleActivate(u)}
                              title="Reactivar" data-testid={`btn-act-${u.id}`}>
                        <Power className="w-4 h-4 text-emerald-600" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && (
        <UserFormModal
          mode="create"
          catalog={catalog}
          authHeaders={authHeaders}
          onClose={() => setCreating(false)}
          onSaved={handleCreated}
        />
      )}
      {editing && (
        <UserFormModal
          mode="edit"
          user={editing}
          catalog={catalog}
          authHeaders={authHeaders}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchUsers(); }}
        />
      )}
      {tempPasswordModal && (
        <TempPasswordModal
          {...tempPasswordModal}
          onClose={() => setTempPasswordModal(null)}
        />
      )}
      {confirmAction?.kind === "deactivate" && (
        <Dialog open onOpenChange={() => setConfirmAction(null)}>
          <DialogContent data-testid="confirm-deactivate">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" /> Confirmar desactivación
              </DialogTitle>
              <DialogDescription>
                Esta acción desactivará a <strong>{confirmAction.user.nombre} {confirmAction.user.apellido}</strong>,
                cerrará todas sus sesiones activas e impedirá nuevos accesos.
                Puedes reactivarlo en cualquier momento.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmAction(null)}>Cancelar</Button>
              <Button className="bg-red-600 hover:bg-red-700 text-white"
                      onClick={() => handleDeactivate(confirmAction.user)}
                      data-testid="confirm-deactivate-yes">
                Sí, desactivar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};


// ============ COMPONENTES INTERNOS ============
const PermisosBadges = ({ permisos, permisosSubmodulos, role }) => {
  if (role === "admin") return <span className="text-xs text-red-600 font-semibold">Acceso total (bypass)</span>;
  if (role === "auditor") return <span className="text-xs text-amber-600">Lectura/Export en todos los módulos</span>;
  const permModulos = Object.keys(permisos || {});
  const subModulos = Object.keys(permisosSubmodulos || {});
  const todos = [...new Set([...permModulos, ...subModulos])];
  if (todos.length === 0)
    return <span className="text-xs text-slate-400 italic">Sin permisos asignados</span>;
  const visible = todos.slice(0, 3);
  const rest = todos.length - visible.length;
  return (
    <div className="flex flex-wrap gap-1 max-w-md">
      {visible.map((mod) => {
        const subs = (permisosSubmodulos || {})[mod];
        if (subs && typeof subs === "object" && !Array.isArray(subs)) {
          const nSub = Object.keys(subs).length;
          return (
            <span key={mod} className="text-xs bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded">
              {mod}: <span className="font-mono">{nSub} sub</span>
            </span>
          );
        }
        const acts = (permisos || {})[mod] || [];
        return (
          <span key={mod} className="text-xs bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded">
            {mod}: <span className="font-mono">{acts.length}</span>
          </span>
        );
      })}
      {rest > 0 && (
        <span className="text-xs text-slate-500">+{rest} más</span>
      )}
    </div>
  );
};


const TempPasswordModal = ({ email, temp_password, title, sessions_revoked, onClose }) => {
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(temp_password);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="temp-password-modal">
        <DialogHeader>
          <DialogTitle className="text-emerald-700">{title}</DialogTitle>
          <DialogDescription>
            Comparte esta clave con el funcionario por un canal seguro (NO email).
            Es la <strong>única vez</strong> que se mostrará y deberá cambiarla en su primer acceso.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs text-slate-500">Email</Label>
            <div className="font-mono text-sm bg-slate-50 px-3 py-2 rounded border">{email}</div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Clave temporal</Label>
            <div className="flex gap-2 items-center">
              <code className="flex-1 font-mono text-base bg-amber-50 border-2 border-amber-300 px-3 py-2 rounded select-all"
                    data-testid="temp-password-value">
                {show ? temp_password : "•".repeat(temp_password.length)}
              </code>
              <Button variant="outline" size="icon" onClick={() => setShow(!show)} title={show ? "Ocultar" : "Mostrar"}>
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
              <Button variant="outline" size="icon" onClick={copy} title="Copiar" data-testid="btn-copy-pwd">
                {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>
          {sessions_revoked != null && sessions_revoked > 0 && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              Se cerraron {sessions_revoked} sesión(es) activa(s) por seguridad.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button onClick={onClose} className="bg-blue-700 text-white" data-testid="btn-close-temp">
            Entendido, ya la guardé
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


const UserFormModal = ({ mode, user, catalog, authHeaders, onClose, onSaved }) => {
  const [form, setForm] = useState(() => ({
    nombre: user?.nombre || "",
    apellido: user?.apellido || "",
    cedula: user?.cedula || "",
    email: user?.email || "",
    role: user?.role || "funcionario",
    preset: "",
    preset_label: user?.preset_label || "",
    permisos: user?.permisos || {},
    permisos_submodulos: user?.permisos_submodulos || {},
  }));
  const [saving, setSaving] = useState(false);
  const [domain, setDomain] = useState("municipio.gov.ve");

  const previewEmail = useMemo(() => {
    if (mode === "edit") return form.email;
    if (form.email) return form.email;
    const slug = (s) => (s || "").toLowerCase().normalize("NFKD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "");
    if (form.nombre && form.apellido)
      return `${slug(form.nombre)}.${slug(form.apellido)}@${domain}`;
    return "";
  }, [form.nombre, form.apellido, form.email, mode, domain]);

  const applyPreset = (presetKey) => {
    if (!presetKey) return;
    const presetPermisos = catalog.presets[presetKey];
    if (!presetPermisos) return;
    // Separar entre módulos con/sin submódulos
    const submodMap = {};
    (catalog.modules || []).forEach((m) => {
      submodMap[m.key] = (m.submodules || []).map((s) => s.key);
    });
    const nuevoPermisos = {};
    const nuevoSub = {};
    Object.entries(presetPermisos).forEach(([modKey, actions]) => {
      const subs = submodMap[modKey] || [];
      if (subs.length === 0) {
        nuevoPermisos[modKey] = [...actions].sort();
      } else {
        // Expandir acciones a TODOS los submódulos
        nuevoSub[modKey] = {};
        subs.forEach((sk) => {
          nuevoSub[modKey][sk] = [...actions].sort();
        });
      }
    });
    setForm((f) => ({
      ...f, preset: presetKey,
      preset_label: PRESET_LABELS[presetKey] || presetKey,
      permisos: nuevoPermisos,
      permisos_submodulos: nuevoSub,
    }));
  };

  const togglePermission = (mod, action) => {
    setForm((f) => {
      const currentActs = f.permisos[mod] || [];
      const newActs = currentActs.includes(action)
        ? currentActs.filter((a) => a !== action)
        : [...currentActs, action].sort();
      const next = { ...f.permisos };
      if (newActs.length === 0) delete next[mod];
      else next[mod] = newActs;
      return { ...f, permisos: next, preset: "" };
    });
  };

  const toggleSubmoduleAction = (mod, subKey, action) => {
    setForm((f) => {
      const modSubs = { ...(f.permisos_submodulos[mod] || {}) };
      const currentActs = modSubs[subKey] || [];
      const newActs = currentActs.includes(action)
        ? currentActs.filter((a) => a !== action)
        : [...currentActs, action].sort();
      if (newActs.length === 0) delete modSubs[subKey];
      else modSubs[subKey] = newActs;
      const nextSub = { ...f.permisos_submodulos };
      if (Object.keys(modSubs).length === 0) delete nextSub[mod];
      else nextSub[mod] = modSubs;
      return { ...f, permisos_submodulos: nextSub, preset: "" };
    });
  };

  const isSubmoduleActionChecked = (mod, subKey, action) => {
    const acts = (form.permisos_submodulos[mod] || {})[subKey] || [];
    return acts.includes(action);
  };

  const toggleSubmoduleRowAll = (mod, subKey, allowedActions) => {
    setForm((f) => {
      const modSubs = { ...(f.permisos_submodulos[mod] || {}) };
      const current = modSubs[subKey] || [];
      const allChecked = allowedActions.every((a) => current.includes(a));
      if (allChecked) {
        delete modSubs[subKey];
      } else {
        modSubs[subKey] = [...allowedActions].sort();
      }
      const nextSub = { ...f.permisos_submodulos };
      if (Object.keys(modSubs).length === 0) delete nextSub[mod];
      else nextSub[mod] = modSubs;
      return { ...f, permisos_submodulos: nextSub, preset: "" };
    });
  };

  const submit = async () => {
    if (!form.nombre.trim() || !form.apellido.trim()) {
      toast.error("Nombre y apellido son obligatorios");
      return;
    }
    setSaving(true);
    try {
      if (mode === "create") {
        const payload = {
          nombre: form.nombre.trim(), apellido: form.apellido.trim(),
          cedula: form.cedula.trim() || null,
          email: form.email.trim() || null,
          role: form.role,
          domain,
          preset: null, // Backend no debe re-aplicar preset; ya lo expandimos en frontend.
          preset_label: form.preset_label || null,
          permisos: form.permisos,
          permisos_submodulos: form.permisos_submodulos,
        };
        const r = await axios.post(`${API}/admin/users`, payload, { headers: authHeaders });
        toast.success("Funcionario creado");
        onSaved(r.data);
      } else {
        const payload = {
          nombre: form.nombre.trim(),
          apellido: form.apellido.trim(),
          email: form.email.trim().toLowerCase(),
          role: form.role,
          permisos: form.permisos,
          permisos_submodulos: form.permisos_submodulos,
          preset_label: form.preset_label || null,
        };
        const r = await axios.put(`${API}/admin/users/${user.id}`, payload, { headers: authHeaders });
        toast.success("Cambios guardados");
        onSaved(r.data);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Error al guardar";
      toast.error(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="user-form-modal">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Nuevo funcionario" : `Editar: ${user.nombre} ${user.apellido}`}
          </DialogTitle>
          <DialogDescription>
            {mode === "create"
              ? "Se generará una clave temporal que el funcionario deberá cambiar en su primer acceso."
              : "Modifica los datos básicos y la matriz de permisos por módulo."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Datos básicos */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Nombre</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                     placeholder="Juan" data-testid="form-nombre" />
            </div>
            <div>
              <Label>Apellido</Label>
              <Input value={form.apellido} onChange={(e) => setForm({ ...form, apellido: e.target.value })}
                     placeholder="Pérez" data-testid="form-apellido" />
            </div>
            <div>
              <Label>Cédula <span className="text-xs text-slate-400">(opcional)</span></Label>
              <Input value={form.cedula} onChange={(e) => setForm({ ...form, cedula: e.target.value })}
                     placeholder="V-12345678" />
            </div>
            <div>
              <Label>Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="form-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="funcionario">Funcionario</SelectItem>
                  <SelectItem value="auditor">Auditor (lectura total)</SelectItem>
                  <SelectItem value="admin">Administrador (acceso total)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>
                Email {mode === "create" && <span className="text-xs text-slate-400">(opcional, se autogenera)</span>}
              </Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                     placeholder={previewEmail} data-testid="form-email"
                     disabled={mode === "edit" ? false : false} />
              {mode === "create" && previewEmail && !form.email && (
                <p className="text-xs text-slate-500 mt-1">
                  Se generará: <code className="bg-slate-100 px-1 rounded">{previewEmail}</code>
                </p>
              )}
            </div>
          </div>

          {/* Preset */}
          {form.role === "funcionario" && (
            <div className="bg-blue-50 border border-blue-200 rounded p-3">
              <Label className="text-blue-900">Plantilla rápida (preset)</Label>
              <p className="text-xs text-slate-600 mb-2">
                Aplica un set predefinido de permisos según el rol operativo.
                Puedes ajustar a mano después.
              </p>
              <Select value={form.preset} onValueChange={applyPreset}>
                <SelectTrigger data-testid="form-preset">
                  <SelectValue placeholder="Sin preset (configurar manualmente)" />
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(catalog.presets || {}).map((k) => (
                    <SelectItem key={k} value={k}>{PRESET_LABELS[k] || k}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                className="mt-2 text-sm"
                placeholder="Etiqueta legible (ej: Cajero principal de Petare)"
                value={form.preset_label}
                onChange={(e) => setForm({ ...form, preset_label: e.target.value })}
                data-testid="form-preset-label"
              />
            </div>
          )}

          {/* Matriz de permisos */}
          {form.role === "funcionario" && (
            <div>
              <Label className="text-base font-semibold">Matriz de permisos por módulo</Label>
              <p className="text-xs text-slate-500 mb-2">
                Marca las acciones que este funcionario puede ejecutar en cada módulo.
                Los módulos sin marcar quedan inaccesibles.
              </p>
              <div className="border border-slate-200 rounded overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="text-left px-3 py-2">Módulo</th>
                      <th className="px-2 py-2">Ver</th>
                      <th className="px-2 py-2">Crear</th>
                      <th className="px-2 py-2">Editar</th>
                      <th className="px-2 py-2">Eliminar</th>
                      <th className="px-2 py-2">Aprobar</th>
                      <th className="px-2 py-2">Exportar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(catalog.modules || []).map((m) => {
                      const submods = m.submodules || [];
                      const hasSubmods = submods.length > 0;
                      const ACTS = ["read", "create", "update", "delete", "approve", "export"];

                      if (!hasSubmods) {
                        // Módulos sin submódulos: 1 fila con 6 checkboxes a nivel módulo
                        const acts = form.permisos[m.key] || [];
                        return (
                          <tr key={m.key} className="border-t border-slate-100">
                            <td className="px-3 py-2">
                              <div className="font-semibold text-slate-800">{m.label}</div>
                              <div className="text-[10px] text-slate-400">{m.description}</div>
                            </td>
                            {ACTS.map((act) => {
                              const allowed = m.actions.includes(act);
                              const checked = acts.includes(act);
                              return (
                                <td key={act} className="text-center px-2 py-2">
                                  {allowed ? (
                                    <input type="checkbox"
                                           checked={checked}
                                           onChange={() => togglePermission(m.key, act)}
                                           className="w-4 h-4 accent-blue-700"
                                           data-testid={`perm-${m.key}-${act}`} />
                                  ) : (
                                    <span className="text-slate-300">—</span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      }

                      // Módulos con submódulos: header + 1 fila por submódulo
                      const allowedActions = m.actions.filter((a) => ACTS.includes(a));
                      return (
                        <Fragment key={m.key}>
                          <tr className="border-t-2 border-slate-200 bg-slate-100/60">
                            <td className="px-3 py-2">
                              <div className="font-semibold text-slate-800">{m.label}</div>
                              <div className="text-[10px] text-slate-400">{m.description}</div>
                            </td>
                            <td colSpan={6} className="px-3 py-2 text-[10px] uppercase tracking-wide text-slate-500 text-right">
                              Configurar acciones por submódulo ↓
                            </td>
                          </tr>
                          {submods.map((s) => {
                            const subActs = (form.permisos_submodulos[m.key] || {})[s.key] || [];
                            const allRowChecked = allowedActions.every((a) => subActs.includes(a));
                            return (
                              <tr key={`${m.key}-${s.key}`} className="border-t border-slate-100 hover:bg-slate-50/40">
                                <td className="px-3 py-2 pl-8">
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() => toggleSubmoduleRowAll(m.key, s.key, allowedActions)}
                                      className="text-[10px] uppercase tracking-wide text-blue-700 hover:underline"
                                      title={allRowChecked ? "Quitar todos" : "Marcar todos"}
                                      data-testid={`submod-toggle-all-${m.key}-${s.key}`}
                                    >
                                      {allRowChecked ? "−" : "+"}
                                    </button>
                                    <span className="text-slate-700">{s.label}</span>
                                  </div>
                                </td>
                                {ACTS.map((act) => {
                                  const allowed = m.actions.includes(act);
                                  const checked = isSubmoduleActionChecked(m.key, s.key, act);
                                  return (
                                    <td key={act} className="text-center px-2 py-2">
                                      {allowed ? (
                                        <input type="checkbox"
                                               checked={checked}
                                               onChange={() => toggleSubmoduleAction(m.key, s.key, act)}
                                               className="w-4 h-4 accent-blue-700"
                                               data-testid={`subperm-${m.key}-${s.key}-${act}`} />
                                      ) : (
                                        <span className="text-slate-300">—</span>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {form.role === "admin" && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
              <strong>Atención:</strong> el rol Administrador concede acceso total
              (incluyendo gestión de funcionarios y configuración de seguridad).
              Asígnalo solo al equipo de TI.
            </div>
          )}
          {form.role === "auditor" && (
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900">
              El rol Auditor tiene <strong>lectura y export</strong> automáticos en todos los módulos
              del catálogo, incluyendo el audit log forense. Sin permisos modificables.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving}
                  className="bg-blue-700 text-white hover:bg-blue-800"
                  data-testid="btn-save-user">
            {saving ? "Guardando..." : (mode === "create" ? "Crear funcionario" : "Guardar cambios")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


export default AdminUsuariosPage;
